"use client";
import React, { createContext, useContext, useState, useMemo } from "react";
import { SelectedServiceDTO } from "@/app/components/Booking/MemberBooking/types";
import type { DbDateString, DbTimeString } from "@openings/api-client";
import type { CompletionMode } from "@/app/components/Booking/AppointmentModal/types";
import type { MemberTagView } from "@openings/template-components";

// Define AppointmentForm and AppState
export interface AppointmentForm {
  userId: string;
  salonId: string;
  scheduleId: string;
  services: SelectedServiceDTO[];
  date: DbDateString;
  time: DbTimeString;
  /**
   * IANA zone the openings were computed in (the schedule's own, else the
   * business's). The confirmation's calendar event reads `date` + `time` in
   * it instead of the customer's device zone (BF-DRIFT-016); when it is
   * missing the device zone is still used.
   */
  timezone?: string;
  stylistName: string;
  /**
   * The booked team member's tags; review and confirmation show the ones
   * with a description (plans/team-member-tags-spec.md).
   */
  memberTags?: readonly MemberTagView[] | null;
  scheduleName?: string;
  /** Physical address of the selected schedule. */
  scheduleAddress?: string;
  /** @deprecated Use scheduleName */
  locationName?: string;
  requestId?: string;
  /** Exact public quote revision paired with requestId for quote bookings. */
  quotePublicId?: string;
  /**
   * The team member performing the appointment. Forwarded to the customer gate
   * pre-check so staff-scope tags are evaluated before the booking is
   * submitted.
   */
  teamMemberId?: string | null;
  cancellationPolicyEnabled?: boolean;
  cancellationPolicyHours?: number | null;
  cancellationPolicyNote?: string | null;
  lateGraceMinutes?: number | null;
  verificationMethod?: "SMS" | "EMAIL";
  onAfter?: (appointmentId: string) => void;
  /**
   * Fires when appointment creation reached the processor but the payment
   * outcome is not settled yet. Quote pages use this to replace availability
   * with a fail-closed pending state immediately.
   */
  onPaymentPending?: () => void;
  /**
   * Fires as soon as the server confirms the booking, before any inline
   * confirmation screen or redirect. Used by the default booking page to
   * refresh time slots once the modal is dismissed.
   */
  onBookingComplete?: () => void;
  /**
   * Controls post-booking behavior.
   * - "redirect" (default): navigate to /appointment/review page (classic multi-page templates)
   * - "inline": show confirmation inside the modal/drawer (light single-page templates, booking page)
   */
  completionMode?: CompletionMode;
}

interface AppState {
  isAppointmentModalActive: boolean;
  appointmentForm?: AppointmentForm;
  date: Date;
}

interface IAppContext {
  appointmentForm?: AppointmentForm;
  isAppointmentModalActive: boolean;
  date: Date;
  openAppointmentForm: (form: AppointmentForm) => void;
  closeAppointmentForm: () => void;
  setDate: (date: Date) => void;
}

// Initial state
const initialState: AppState = {
  isAppointmentModalActive: false,
  appointmentForm: undefined,
  date: new Date(),
};

// Create context
export const AppContext = createContext<IAppContext>({
  appointmentForm: undefined,
  isAppointmentModalActive: false,
  date: new Date(),
  openAppointmentForm: () => {},
  closeAppointmentForm: () => {},
  setDate: () => {},
});

export const AppContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, setState] = useState<AppState>(initialState);

  // Functions for updating state
  const openAppointmentForm = (form: AppointmentForm) => {
    setState((prev) => ({
      ...prev,
      isAppointmentModalActive: true,
      appointmentForm: form,
    }));
  };

  const closeAppointmentForm = () => {
    setState((prev) => ({
      ...prev,
      isAppointmentModalActive: false,
      appointmentForm: undefined,
    }));
  };

  const setDate = (date: Date) => {
    setState((prev) => ({ ...prev, date }));
  };

  // ✅ Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      appointmentForm: state.appointmentForm,
      isAppointmentModalActive: state.isAppointmentModalActive,
      date: state.date,
      openAppointmentForm,
      closeAppointmentForm,
      setDate,
    }),
    [state.appointmentForm, state.isAppointmentModalActive, state.date],
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// Custom hook to use the context
export const useApp = () => useContext(AppContext);
